import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Button,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  useColorModeValue,
  Divider,
  Select,
  Skeleton,
  Grid,
  GridItem,
  Spinner,
  Icon
} from '@chakra-ui/react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaVideo } from 'react-icons/fa';
import ProductCard from './ProductCards/ProductCard';
import Loading from './resources/Loading';
import { useMediaQuery } from '@chakra-ui/react';
import PmsbyScheme from './components/PmsbyScheme/PmsbyScheme';
import BuyPashuAhar from './pages/BuyPashuAhar';
import { useTranslation } from './translations/useTranslation';
import SellAnimal from './components/SellAnimal';
import { API_BASE_URL } from './config/api_keys';

const LivestockPage = () => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [activeTab, setActiveTab] = useState(0);
  const [products, setProducts] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnimalType, setSelectedAnimalType] = useState('all');
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBgColor = useColorModeValue('green.50', 'green.900');
  
  // Animal type filter options
  const animalTypes = [
    { value: 'all', label: 'All Animals' },
    { value: 'cow', label: 'Cows' },
    { value: 'buffalo', label: 'Buffaloes' },
    { value: 'goat', label: 'Goats' },
    { value: 'sheep', label: 'Sheep' },
    { value: 'poultry', label: 'Poultry' },
    { value: 'other', label: 'Other' }
  ];
  const navigate = useNavigate();
  const [sellType, setSellType] = useState('Cows');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch registered animals for sale
        const animalsResponse = await axios.get(`${API_BASE_URL}/api/animals/sell`);
        console.log('Fetched animals:', animalsResponse.data);
        setAnimals(animalsResponse.data || []);
        
        // Also fetch livestock products (for backward compatibility)
        try {
          const productsResponse = await axios.get("https://api.agromanch.com/getProducts");
          const livestockProducts = productsResponse.data.filter(product => 
            product.catagory === "Livestock"
          );
          setProducts(livestockProducts);
        } catch (productErr) {
          console.error("Error fetching livestock products:", productErr);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching animals:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter animals based on selected animal type
  const filteredAnimals = selectedAnimalType === 'all' 
    ? animals 
    : animals.filter(animal => {
        const animalTypeLower = animal.animalType?.toLowerCase() || '';
        const selectedTypeLower = selectedAnimalType.toLowerCase();
        // Map filter values to actual animal types (Animal model uses: Cow, Buffalo, Goat, Sheep, Poultry, Other)
        const typeMap = {
          'cow': 'cow',
          'buffalo': 'buffalo',
          'goat': 'goat',
          'sheep': 'sheep',
          'poultry': 'poultry',
          'other': 'other'
        };
        // Compare case-insensitively
        return animalTypeLower === typeMap[selectedTypeLower] || 
               animalTypeLower === selectedTypeLower;
      });

  // Function to handle animal type filter change
  const handleAnimalTypeChange = (e) => {
    setSelectedAnimalType(e.target.value);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box>
      <Container maxW="container.xl" py={6}>     
        {/* Main Options - single row, compact tiles with horizontal scroll on overflow */}
        <Box mb={6}>
          <Flex overflowX="auto" py={1} css={{ '&::-webkit-scrollbar': { height: '8px' } }}>
            <HStack spacing={3} px={2} minW="max-content">
              {/* Buy Livestock Option */}
              <Box
                as="button"
                onClick={() => setActiveTab(0)}
                minW={{ base: '120px', md: '150px' }}
                p={2}
                borderRadius="xl"
                bg={activeTab === 0 ? 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))' : bgColor}
                color={activeTab === 0 ? 'white' : 'inherit'}
                boxShadow={activeTab === 0 ? 'lg' : 'sm'}
                cursor="pointer"
                textAlign="center"
              >
                <VStack spacing={1}>
                  <Image src="https://cdn-icons-png.flaticon.com/512/2395/2395796.png" alt="Buy" boxSize={{ base: '28px', md: '34px' }} opacity={activeTab === 0 ? 1 : 0.8} />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold">{t('buyLivestock') || 'Buy Animals'}</Text>
                </VStack>
              </Box>

              {/* Sell Livestock Option */}
               <Box
                as="button"
                onClick={() => setActiveTab(3)}
                minW={{ base: '120px', md: '150px' }}
                p={2}
                borderRadius="xl"
                bg={activeTab === 3 ? 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))' : bgColor}
                color={activeTab === 3 ? 'white' : 'inherit'}
                boxShadow={activeTab === 3 ? 'lg' : 'sm'}
                cursor="pointer"
                textAlign="center"
              >
                <VStack spacing={1}>
                  <Image src="https://cdn-icons-png.flaticon.com/512/2933/2933193.png" alt="Sell" boxSize={{ base: '28px', md: '34px' }} opacity={activeTab === 3 ? 1 : 0.8} />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold">{t('sellLivestock') || 'Sell Livestock'}</Text>
                </VStack>
              </Box>

              {/* Animal Feed Option */}
              <Box
                as="button"
                onClick={() => setActiveTab(1)}
                minW={{ base: '120px', md: '150px' }}
                p={2}
                borderRadius="xl"
                bg={activeTab === 1 ? 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))' : bgColor}
                color={activeTab === 1 ? 'white' : 'inherit'}
                boxShadow={activeTab === 1 ? 'lg' : 'sm'}
                cursor="pointer"
                textAlign="center"
              >
                <VStack spacing={1}>
                  <Image src="https://cdn-icons-png.flaticon.com/512/3081/3081120.png" alt="Feed" boxSize={{ base: '28px', md: '34px' }} opacity={activeTab === 1 ? 1 : 0.8} />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold">{t('animalFeed') || 'Animal Feed'}</Text>
                </VStack>
              </Box>

              {/* Livestock Insurance Option */}
              <Box
                as="button"
                onClick={() => setActiveTab(2)}
                minW={{ base: '120px', md: '150px' }}
                p={2}
                borderRadius="xl"
                bg={activeTab === 2 ? 'linear-gradient(to right, var(--chakra-colors-green-400), var(--chakra-colors-teal-500))' : bgColor}
                color={activeTab === 2 ? 'white' : 'inherit'}
                boxShadow={activeTab === 2 ? 'lg' : 'sm'}
                cursor="pointer"
                textAlign="center"
              >
                <VStack spacing={1}>
                  <Image src="https://cdn-icons-png.flaticon.com/512/2910/2910914.png" alt="Insurance" boxSize={{ base: '28px', md: '34px' }} opacity={activeTab === 2 ? 1 : 0.8} />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold">{t('livestockInsurance') || 'Livestock Insurance'}</Text>
                </VStack>
              </Box>

             
             
            </HStack>
          </Flex>
        </Box>
        
        {/* Content Based on Selected Tab */}
        <Box>
          {activeTab === 0 && (
            <Box>      
              {/* Animal Type Quick Filters */}
              <Flex 
                overflowX="auto" 
                py={2} 
                mb={6} 
                css={{
                  '&::-webkit-scrollbar': { height: '6px' },
                  '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                  '&::-webkit-scrollbar-thumb': { background: '#c5c5c5', borderRadius: '10px' }
                }}
              >
                <HStack spacing={3} minW="max-content" px={2}>
                  {animalTypes.map(type => (
                    <Button 
                      key={type.value}
                      size="sm"
                      variant={selectedAnimalType === type.value ? "solid" : "outline"}
                      colorScheme="green"
                      onClick={() => setSelectedAnimalType(type.value)}
                      borderRadius="full"
                      px={4}
                    >
                      {type.label}
                    </Button>
                  ))}
                </HStack>
              </Flex>
              
              {/* Animals for Sale Grid */}
              {filteredAnimals.length > 0 ? (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {filteredAnimals.map(animal => (
                    <Box 
                      key={animal._id}
                      borderWidth="1px" 
                      borderRadius="lg" 
                      overflow="hidden"
                      bg={bgColor}
                      borderColor={borderColor}
                      transition="all 0.3s"
                      _hover={{ transform: 'translateY(-3px)', boxShadow: 'lg' }}
                      cursor="pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/animals/${animal._id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/animals/${animal._id}`); }}
                    >
                      <VStack align="stretch" spacing={0}>
                        {/* Animal Image */}
                        <Box position="relative" h="200px" overflow="hidden">
                          {animal.images && animal.images.length > 0 ? (
                            <Image 
                              src={animal.images[0]} 
                              alt={animal.title}
                              w="100%"
                              h="100%"
                              objectFit="cover"
                            />
                          ) : animal.udderPhotoUrl ? (
                            <Image 
                              src={animal.udderPhotoUrl} 
                              alt={animal.title}
                              w="100%"
                              h="100%"
                              objectFit="cover"
                            />
                          ) : animal.videoUrl ? (
                            <Box w="100%" h="100%" position="relative">
                              <video
                                src={animal.videoUrl}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                muted
                                playsInline
                                onMouseEnter={(e) => e.target.play()}
                                onMouseLeave={(e) => {
                                  e.target.pause();
                                  e.target.currentTime = 0;
                                }}
                              />
                              <Box
                                position="absolute"
                                top="50%"
                                left="50%"
                                transform="translate(-50%, -50%)"
                                bg="blackAlpha.600"
                                borderRadius="full"
                                p={2}
                              >
                                <Icon as={FaVideo} color="white" boxSize={6} />
                              </Box>
                            </Box>
                          ) : (
                            <Box 
                              bg="gray.200" 
                              w="100%" 
                              h="100%"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Text color="gray.500">No Image</Text>
                            </Box>
                          )}
                          <Badge 
                            position="absolute" 
                            top={2} 
                            right={2} 
                            colorScheme="green"
                          >
                            {animal.animalType}
                          </Badge>
                        </Box>
                        
                        {/* Animal Details */}
                        <Box p={4}>
                          <Heading size="md" mb={2} noOfLines={1}>
                            {animal.title}
                          </Heading>
                          <Text fontSize="sm" color="gray.600" mb={2} noOfLines={2}>
                            {animal.description}
                          </Text>
                          <HStack justify="space-between" mt={3}>
                            <Text fontSize="xl" fontWeight="bold" color="green.600">
                              ₹{animal.price?.toLocaleString()}
                            </Text>
                            {animal.address && (
                              <Text fontSize="xs" color="gray.500">
                                {animal.address.city}, {animal.address.state}
                              </Text>
                            )}
                          </HStack>
                          {animal.currentMilkPerDay && (
                            <HStack mt={2}>
                              <Text fontSize="sm" color="gray.600">
                                Milk: {animal.currentMilkPerDay} L/day
                              </Text>
                            </HStack>
                          )}
                        </Box>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              ) : (
                <Box textAlign="center" py={10}>
                  <Text fontSize="lg" color="gray.500">
                    No animals found in this category.
                  </Text>
                  <Text fontSize="sm" color="gray.400" mt={2}>
                    Be the first to list an animal for sale!
                  </Text>
                </Box>
              )}
            </Box>
          )}

          {/* Sell livestock UI: show SellAnimal component when Sell tab is active */}
          {activeTab === 3 && (
            <Box>
              <SellAnimal animalType={sellType} />
            </Box>
          )}
          
          {activeTab === 1 && <BuyPashuAhar />}
          {activeTab === 2 && <PmsbyScheme />}
        </Box>
      </Container>
    </Box>
  );
};

export default LivestockPage;